import { useCallback, useContext, useEffect, useState } from "react"
import _ from "lodash"

// Context
import { UserContext } from "../../../context/userContext"

//PrimeReact
import { Dropdown } from "primereact/dropdown"
import { MultiSelect } from "primereact/multiselect"

//Styles
import myStyles from "../../../styles/assessments/AssessmentBlock.module.css"
import { useLazyQuery } from "@apollo/client"
import { GET_LOCATIONS_PROGRAMS } from "../queries"
import { LogError } from "../../../helpers/logger"
import { Calendar } from "primereact/calendar"
import { classNames } from "primereact/utils"

export default function AssessmentBlockSession({
  locationId,
  onLocationIdChange,
  sessionDate,
  onSessionDateChange,
  activePrograms,
  onChangeActivePrograms,
  readOnly,
}) {
  const userCtx = useContext(UserContext)

  const [isLoading, setIsLoading] = useState(true)
  const [locations, setLocations] = useState([])
  const [programs, setPrograms] = useState([])
  const [placeholder, setPlaceHolder] = useState("Loading...")

  const [loadDataQuery] = useLazyQuery(GET_LOCATIONS_PROGRAMS, {
    fetchPolicy: "cache-and-network",
    variables: {
      participant_id: userCtx.participant_id,
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
    },
  })

  const loadData = useCallback(async () => {
    try {
      const results = await loadDataQuery({
        variables: {
          participant_id: userCtx.participant_id,
          staff_id: userCtx.session.user.staff_id,
          organization_id: userCtx.session.user.organization_id,
        },
      })

      if (results.data) {
        if (results.data.locations) {
          setLocations(
            results.data.locations.filter(
              (location) => location.location_status === "ACTIVE",
            ) || [],
          )
        } else {
          setLocations([])
        }

        let availablePrograms = []
        if (results.data.programs) {
          availablePrograms =
            results.data.programs
              .filter((program) => program.program_id_status === "ACTIVE")
              .map((program) => ({
                id: program.program_identity_id,
                name: program.program_id_name,
              })) || []

          setPrograms(availablePrograms)
        } else {
          setPrograms([])

          //If there are no programs, we should clear out any active programs since they would no longer be valid selections
          if (!_.isEmpty(activePrograms)) {
            onChangeActivePrograms([])
          }
        }

        if (
          results.data.participantPrograms &&
          results.data.participantPrograms.length > 0 &&
          _.isEmpty(activePrograms) &&
          availablePrograms.length > 0
        ) {
          const newActivePrograms = results.data.participantPrograms
            .filter((program) => program.program_id_status === "ACTIVE")
            .map((program) => ({
              id: program.program_identity_id,
              name: program.program_id_name,
            }))

          onChangeActivePrograms(newActivePrograms)
        }

        setPlaceHolder("Please select an option")
        setIsLoading(false)
      } else {
        LogError(
          "Unable to load the active locations and programs for this organization: No data returned",
        )
        userCtx.setNotificationError(
          "Unable to load available locations and programs",
        )
        setLocations([])
        setPlaceHolder("No values available")
        setIsLoading(false)
      }
    } catch (error) {
      LogError(
        "Unable to load the active locations and programs for this organization",
        error,
      )
      userCtx.setNotificationError(
        "Unable to load available locations and programs",
      )
      setLocations([])
      setIsLoading(false)
      setPlaceHolder("No values available")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.session.user.organization_id])

  useEffect(() => {
    loadData()
  }, [loadData])

  return (
    <div className={myStyles.sessionLayout}>
      <div className={`${myStyles.sessionLayoutTitle} ${myStyles.title}`}>
        Session
      </div>
      <div className={myStyles.sessionLayoutDate}>
        <div>Session date</div>
        <div style={{ marginBottom: "10px" }}>
          <small style={{ color: "gray" }}>
            {"The date for which this session is being captured"}
          </small>
        </div>
        <div className={myStyles.sessionDropdownContainer}>
          <Calendar
            value={sessionDate}
            onChange={(e) => {
              onSessionDateChange(e.value)
            }}
            className={myStyles.sessionDate}
            disabled={readOnly}
          />
        </div>
      </div>
      <div className={myStyles.sessionLayoutLocation}>
        <div>Location</div>
        <div style={{ marginBottom: "10px" }}>
          <small style={{ color: "gray" }}>
            {"Specify the location to be captured for this session"}
          </small>
        </div>
        <div className={myStyles.sessionDropdownContainer}>
          <Dropdown
            className={myStyles.sessionDropDown}
            showClear
            disabled={isLoading || readOnly}
            options={locations}
            optionLabel="location_name"
            placeholder={placeholder}
            value={locationId}
            onChange={(e) => {
              onLocationIdChange(e.value)
            }}
          />
        </div>
      </div>
      <div className={myStyles.sessionLayoutProgram}>
        <div>Programs</div>
        <div style={{ marginBottom: "10px" }}>
          <small style={{ color: "gray" }}>
            {"Specify the programs to be captured for this session"}
          </small>
        </div>
        <div className={myStyles.sessionDropdownContainer}>
          <MultiSelect
            className={myStyles.sessionDropDown}
            showClear
            disabled={isLoading || readOnly}
            options={programs}
            optionLabel="name"
            placeholder={placeholder}
            value={activePrograms}
            onChange={(e) => {
              onChangeActivePrograms(e.value || [])
            }}
          />
        </div>
      </div>
    </div>
  )
}
